import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { ToneDetails } from '../types';

interface ToneEQVisualizerProps {
  details?: ToneDetails | null;
  lang?: 'en' | 'fr';
  levelId?: number;
}

export const ToneEQVisualizer: React.FC<ToneEQVisualizerProps> = ({ details, lang = 'fr', levelId = 4 }) => {
  const isFr = lang === 'fr';
  const isExpert = levelId >= 6;

  // Axe 7: Render placeholder if no data
  if (!details) return (
    <div className="bg-[#001D24] p-10 rounded-[60px] border border-white/10 shadow-2xl h-full flex flex-col gap-8 overflow-hidden justify-center items-center opacity-40 text-center">
        <span className="text-6xl mb-4">🎚️</span>
        <h3 className="text-xl font-black text-white uppercase italic">Analyse du Timbre Indisponible</h3>
        <p className="text-[10px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
            Les informations spectrales n'ont pas pu être extraites du signal. Assurez-vous d'avoir un son clair.
        </p>
    </div>
  );

  // Clamp des valeurs pour rester dans le domaine visuel [-100, 100]
  const clampValue = (v: number) => Math.max(-100, Math.min(100, v * 100));

  const data = [
    { 
        name: isFr ? 'BASSES' : 'BASS', 
        value: clampValue(details.deltaLow ?? 0), 
        label: isExpert ? '0-250Hz' : '', 
        displayVal: isExpert ? `${(clampValue(details.deltaLow ?? 0)).toFixed(0)}%` : '' 
    },
    { 
        name: isFr ? 'MÉDIUMS' : 'MIDS', 
        value: clampValue(details.deltaMid ?? 0), 
        label: isExpert ? '250-2.5kHz' : '',
        displayVal: isExpert ? `${(clampValue(details.deltaMid ?? 0)).toFixed(0)}%` : '' 
    },
    { 
        name: isFr ? 'AIGUS' : 'TREBLE', 
        value: clampValue(details.deltaHigh ?? 0), 
        label: isExpert ? '2.5kHz+' : '',
        displayVal: isExpert ? `${(clampValue(details.deltaHigh ?? 0)).toFixed(0)}%` : '' 
    },
  ];

  const getAdvice = () => {
      const advices = [];
      const dL = details.deltaLow ?? 0;
      const dH = details.deltaHigh ?? 0;
      
      if (dL > 0.15) advices.push(isFr ? "Trop de Basses : Éloigne le micro ou baisse le potard 'Bass'." : "Too much Bass: Move mic away or lower 'Bass' knob.");
      if (dL < -0.15) advices.push(isFr ? "Manque de Coffre : Rapproche le micro de la rosace/HP." : "Thin Sound: Move mic closer to soundhole/speaker.");
      if (dH > 0.15) advices.push(isFr ? "Trop d'Aigus : Baisse le Tone ou utilise le micro manche." : "Too Bright: Lower 'Tone' or switch to neck pickup.");
      if (dH < -0.15) advices.push(isFr ? "Son Sourd : Augmente les Trebles ou joue avec un médiator plus dur." : "Muddy Sound: Boost Treble or use a harder pick.");
      
      if (advices.length === 0) return isFr ? "Équilibre parfait ! Ton grain est identique au Master." : "Perfect Balance! Your tone matches the Master.";
      return advices[0]; 
  };

  return (
    <div className="bg-[#001D24] p-10 rounded-[60px] border border-white/10 shadow-2xl h-full flex flex-col gap-8 overflow-hidden">
      <div className="flex justify-between items-start">
        <div className="space-y-1">
            <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter">Tone <span className="text-smart-accent">Fixer</span></h3>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest ml-1">Écart fréquentiel Master vs Vous</p>
        </div>
        <div className="bg-black/40 px-6 py-3 rounded-2xl border border-white/5 max-w-[250px]">
            <span className="text-[8px] font-black text-smart-gold uppercase block mb-1">Action Requise</span>
            <p className="text-[11px] text-white font-bold italic leading-tight">{getAdvice()}</p>
        </div>
      </div>

      <div className="flex-1 min-h-[300px] bg-black/20 rounded-[40px] p-6 border border-white/5 relative">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 20, right: 40 }}>
                {/* Domaine étendu à 100 pour voir les deltas complets */}
                <XAxis type="number" domain={[-100, 100]} hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 900}} width={80} />
                <ReferenceLine x={0} stroke="rgba(255,255,255,0.2)" />
                <Bar dataKey="value" radius={[0, 10, 10, 0]} isAnimationActive={true}>
                    {data.map((entry, index) => (
                        <Cell key={index} fill={entry.value > 0 ? '#FF6261' : '#06b6d4'} fillOpacity={0.8} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
        
        {isExpert && (
            <div className="absolute inset-0 flex flex-col justify-around py-8 pointer-events-none">
                {data.map((d, i) => (
                    <div key={i} className="flex justify-end px-12">
                        <span className="text-[9px] font-mono text-slate-500">{d.label}</span>
                        {d.displayVal && <span className="ml-2 text-[9px] font-black text-white">{d.value > 0 ? '+' : ''}{d.displayVal}</span>}
                    </div>
                ))}
            </div>
        )}
        
        <div className="absolute top-1/2 left-4 -translate-y-1/2 -rotate-90 text-[8px] font-black text-slate-800 uppercase tracking-widest pointer-events-none">Frequence Domain</div>
      </div>

      <div className="flex justify-between px-4">
          <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
              <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Sous-représenté</span>
          </div>
          <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-smart-accent"></div>
              <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Sur-représenté</span>
          </div>
      </div>
    </div>
  );
};